import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {
  useDeleteInsuranceTypeMutation,
  usePostInsuranceTypeSetActiveMutation,
  usePostInsuranceTypeSetDefaultMutation,
  usePostInsuranceTypeSetInactiveMutation,
  usePostInsuranceTypeUnsetDefaultMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WorkshopInsuranceTypes() {
  const navigate = useNavigate();

  const [activateInsuranceType] = usePostInsuranceTypeSetActiveMutation();
  const [deactivateInsuranceType] = usePostInsuranceTypeSetInactiveMutation();
  const [deleteInsuranceType] = useDeleteInsuranceTypeMutation();
  const [setDefaultInsuranceType] = usePostInsuranceTypeSetDefaultMutation();
  const [unsetDefaultInsuranceType] = usePostInsuranceTypeUnsetDefaultMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(composePath(settingsRoutes.workshopInsuranceTypesDetail, {params: {id}}));
      })
      .with('setActive', () => {
        activateInsuranceType({insuranceTypeId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('setInactive', () => {
        deactivateInsuranceType({insuranceTypeId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('setAsDefault', () => {
        setDefaultInsuranceType({insuranceTypeId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('disableDefault', () => {
        unsetDefaultInsuranceType({insuranceTypeId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          onConfirm: () => {
            deleteInsuranceType({insuranceTypeId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError);
          },
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.workshop.labels.insuranceTypes'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.workshop.actions.newInsuranceType'),
        onClick: () => navigate(settingsRoutes.workshopInsuranceTypesCreate),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.workshop.labels.insuranceTypes')} />
      <SettingsTemplate
        data-testid={testIds.settings.workshopInsuranceTypes('template')}
        header={header}
      >
        <DmsDataGrid gridCode="insurance-type" actionCallback={actionCallback} />
      </SettingsTemplate>
    </>
  );
}
